import { useEffect, useState } from 'react';
import { Link, NavLink, Route, Routes } from 'react-router-dom';
import { isLocalMode } from '@/local';
import { getLastEpisode } from '@/services/lastEpisode';
import { AdminLocalPanel } from './AdminLocalPanel';
import { ModelsAdmin } from './admin/ModelsAdmin';
import { Trash } from '@phosphor-icons/react';
import clsx from 'clsx';
import {
  listStylePresets,
  createStylePreset,
  deleteStylePreset,
  setStylePresetDefault,
} from '@/services/stylePresetsApi';
import {
  listGlobalPromptTemplates,
  createGlobalPromptTemplate,
  deleteGlobalPromptTemplate,
} from '@/services/promptTemplatesApi';
import {
  listEpisodeSnapshots,
  restoreEpisodeSnapshot,
  type EpisodeSnapshot,
} from '@/services/snapshotsApi';
import { toast } from '@/state/toastStore';
import type { PromptTemplate, StylePreset, TemplateKind } from '@/types';

const TEMPLATE_KIND_LABELS: Record<TemplateKind, string> = {
  image: 'Image template',
  video: 'Video template',
};

export function AdminPage() {
  const lastEpisode = getLastEpisode();
  const backHref = lastEpisode ? `/episode/${lastEpisode}` : '/';
  const backLabel = lastEpisode ? '← Back to episode' : '← Back to studio';
  return (
    <div className="flex h-full">
      <aside className="w-56 shrink-0 border-r border-line-1 bg-surface-1 p-5">
        <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
          Admin
        </p>
        <nav className="flex flex-col gap-1 text-[13px]">
          <AdminLink to="/admin">Style presets</AdminLink>
          <AdminLink to="/admin/templates">Prompt templates</AdminLink>
          <AdminLink to="/admin/models">Models</AdminLink>
          <AdminLink to="/admin/snapshots">Snapshots</AdminLink>
          <AdminLink to="/admin/local">
            Local mode {isLocalMode() ? '' : '·  off'}
          </AdminLink>
        </nav>
        <Link
          to={backHref}
          className="mt-8 inline-block text-[12px] text-text-4 hover:text-text-2"
        >
          {backLabel}
        </Link>
      </aside>
      <main className="min-w-0 flex-1 overflow-y-auto p-8">
        <Routes>
          <Route index element={<StylePresetsAdmin />} />
          <Route path="templates" element={<TemplatesAdmin />} />
          <Route
            path="models"
            element={
              <Section
                title="Vertex models"
                description="Pick which Vertex model IDs the studio uses for image and video generation. Changes apply to the next generation; presets that previously stored a model are no longer consulted."
              >
                <ModelsAdmin />
              </Section>
            }
          />
          <Route path="snapshots" element={<SnapshotsRouter />} />
          <Route path="local" element={<LocalModeRoute />} />
        </Routes>
      </main>
    </div>
  );
}

function LocalModeRoute() {
  if (!isLocalMode()) {
    return (
      <Section
        title="Local mode"
        description="Vertex setup + local data tools. Only available when the studio is running in local mode."
      >
        <div className="rounded-md border-l-2 border-warn bg-warn/10 p-4 text-[13px] text-text-1">
          <p className="font-medium">Local mode is not active in this session.</p>
          <p className="mt-2 text-text-2">To enable it, do one of:</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-text-2">
            <li>
              Restart the dev server with{' '}
              <code className="font-mono text-text-1">npm run dev:local</code>
            </li>
            <li>
              Reload this page with{' '}
              <code className="font-mono text-text-1">?local=1</code> appended to the URL
              (sticky for the session)
            </li>
            <li>
              Set <code className="font-mono text-text-1">VITE_LOCAL=true</code> in{' '}
              <code className="font-mono text-text-1">.env.local</code>
            </li>
          </ul>
        </div>
      </Section>
    );
  }
  return (
    <Section
      title="Local mode"
      description="Vertex setup + local data tools. Available because this session is running in local mode."
    >
      <AdminLocalPanel />
    </Section>
  );
}

function AdminLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        clsx(
          'rounded-md px-3 py-2 transition-colors',
          isActive
            ? 'bg-accent-soft text-accent'
            : 'text-text-2 hover:bg-surface-2 hover:text-text-1',
        )
      }
    >
      {children}
    </NavLink>
  );
}

// -------- Style presets --------

function StylePresetsAdmin() {
  const [presets, setPresets] = useState<StylePreset[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      setPresets(await listStylePresets());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function setDefault(id: string) {
    try {
      await setStylePresetDefault(id);
      toast.ok('Default preset updated.');
      void refresh();
    } catch (err) {
      toast.err(err instanceof Error ? err.message : 'Update failed');
    }
  }

  return (
    <Section
      title="Style presets"
      description="Global style presets available to every episode. The one marked default is applied to new episodes automatically."
    >
      <CreatePresetForm onCreated={refresh} />
      {error && <p className="mt-4 text-[12px] text-err">{error}</p>}
      <ul className="mt-6 space-y-2">
        {presets.length === 0 && !error && (
          <p className="text-[13px] text-text-4">No style presets yet.</p>
        )}
        {presets.map((p) => (
          <li
            key={p.id}
            className="flex items-start justify-between gap-4 rounded-md bg-surface-2 p-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-[14px] font-medium text-text-1">{p.name}</p>
                {p.isDefault && (
                  <span className="rounded-sm bg-accent-soft px-1.5 py-0.5 font-mono text-[10px] uppercase text-accent">
                    default
                  </span>
                )}
              </div>
              <p className="text-[12px] text-text-3">{p.description}</p>
              <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-[11px] text-text-3">
                <input
                  type="radio"
                  name="default-preset"
                  checked={p.isDefault}
                  onChange={() => setDefault(p.id)}
                  className="h-3 w-3 accent-accent"
                />
                Use as default for new episodes
              </label>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (!confirm(`Delete preset "${p.name}"?`)) return;
                try {
                  await deleteStylePreset(p.id);
                  toast.ok(`Deleted ${p.name}.`);
                  void refresh();
                } catch (err) {
                  toast.err(err instanceof Error ? err.message : 'Delete failed');
                }
              }}
              aria-label={`Delete ${p.name}`}
              className="rounded-md p-1.5 text-text-3 transition-colors hover:bg-surface-3 hover:text-err"
            >
              <Trash size={14} />
            </button>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function CreatePresetForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      // imageModel/videoModel come from /admin/models now and are not per-
      // preset. We omit them in the create payload — the backend defaults
      // to the env values for back-compat columns.
      await createStylePreset({
        name,
        description,
        imageModel: '',
        videoModel: '',
      });
      setName('');
      setDescription('');
      toast.ok('Preset created.');
      onCreated();
    } catch (err) {
      toast.err(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-md bg-surface-1 p-4">
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
        New preset
      </p>
      <Input value={name} onChange={setName} placeholder="Preset name" />
      <Input
        value={description}
        onChange={setDescription}
        placeholder="Short description"
      />
      <p className="text-[11px] text-text-4">
        Vertex models are now configured globally in the{' '}
        <Link to="/admin/models" className="text-accent hover:underline">
          Models
        </Link>{' '}
        section.
      </p>
      <button
        type="submit"
        disabled={submitting || !name.trim()}
        className="rounded-md bg-accent px-4 py-2 text-[13px] font-medium text-text-on-accent transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-surface-3 disabled:text-text-4"
      >
        {submitting ? 'Creating…' : 'Create preset'}
      </button>
    </form>
  );
}

// -------- Templates --------

function TemplatesAdmin() {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      setTemplates(await listGlobalPromptTemplates());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <Section
      title="Prompt templates"
      description="Global templates available to every episode. The two system rows (Default image / Default video) are read-only — clone them inside an episode's Style tab to customize."
    >
      <CreateTemplateForm onCreated={refresh} />
      {error && <p className="mt-4 text-[12px] text-err">{error}</p>}
      <ul className="mt-6 space-y-2">
        {templates.length === 0 && !error && (
          <p className="text-[13px] text-text-4">No global templates yet.</p>
        )}
        {templates.map((t) => (
          <li
            key={t.id}
            className="flex items-start justify-between gap-4 rounded-md bg-surface-2 p-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-[14px] font-medium text-text-1">{t.name}</p>
                <span className="rounded-sm bg-info/20 px-1.5 py-0.5 text-[10px] text-info">
                  {TEMPLATE_KIND_LABELS[t.kind]}
                </span>
                {t.isSystem && (
                  <span className="rounded-sm bg-warn/20 px-1.5 py-0.5 font-mono text-[10px] uppercase text-warn">
                    system
                  </span>
                )}
              </div>
              <p className="mt-1 line-clamp-2 font-mono text-[11px] text-text-3">{t.body}</p>
            </div>
            {!t.isSystem && (
              <button
                type="button"
                onClick={async () => {
                  if (!confirm(`Delete template "${t.name}"?`)) return;
                  try {
                    await deleteGlobalPromptTemplate(t.id);
                    toast.ok(`Deleted ${t.name}.`);
                    void refresh();
                  } catch (err) {
                    toast.err(err instanceof Error ? err.message : 'Delete failed');
                  }
                }}
                aria-label={`Delete ${t.name}`}
                className="rounded-md p-1.5 text-text-3 transition-colors hover:bg-surface-3 hover:text-err"
              >
                <Trash size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function CreateTemplateForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<TemplateKind>('image');
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !body.trim()) return;
    setSubmitting(true);
    try {
      await createGlobalPromptTemplate({ name, body, kind });
      setName('');
      setBody('');
      toast.ok('Template created.');
      onCreated();
    } catch (err) {
      toast.err(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-md bg-surface-1 p-4">
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
        New global template
      </p>
      <Input value={name} onChange={setName} placeholder="Template name" />
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value as TemplateKind)}
        className="w-full rounded-md bg-surface-2 px-3 py-2 text-[13px] text-text-1 outline-none ring-1 ring-line-2 focus:ring-line-3"
      >
        <option value="image">Image template</option>
        <option value="video">Video template</option>
      </select>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        placeholder="Template body. Use {prompt}, {style}, {cultural_guidelines}, {aspect_ratio}, {cast}, {previous_scenes}."
        className="w-full resize-none rounded-md bg-surface-2 px-3 py-2 font-mono text-[12px] text-text-1 placeholder:text-text-4 outline-none ring-1 ring-line-2 focus:ring-line-3"
      />
      <button
        type="submit"
        disabled={submitting || !name.trim() || !body.trim()}
        className="rounded-md bg-accent px-4 py-2 text-[13px] font-medium text-text-on-accent transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-surface-3 disabled:text-text-4"
      >
        {submitting ? 'Creating…' : 'Create template'}
      </button>
    </form>
  );
}

// -------- Snapshots --------

function SnapshotsRouter() {
  const [uuid, setUuid] = useState('');
  return (
    <Section
      title="Episode snapshots"
      description="JSON dumps taken every 100 mutations or daily, capped at 30 per episode. Restore to roll back catastrophic mistakes — restore takes a pre-restore snapshot first, so it's reversible."
    >
      <div className="flex items-center gap-3">
        <Input
          value={uuid}
          onChange={setUuid}
          placeholder="Episode UUID"
          mono
        />
      </div>
      {uuid && <SnapshotsList episodeUuid={uuid} />}
    </Section>
  );
}

function SnapshotsList({ episodeUuid }: { episodeUuid: string }) {
  const [snapshots, setSnapshots] = useState<EpisodeSnapshot[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      setSnapshots(await listEpisodeSnapshots(episodeUuid));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    }
  }

  useEffect(() => {
    void refresh();
  }, [episodeUuid]);

  return (
    <div className="mt-6">
      {error && <p className="text-[12px] text-err">{error}</p>}
      {snapshots.length === 0 && !error && (
        <p className="text-[13px] text-text-4">No snapshots for this episode.</p>
      )}
      <ul className="space-y-2">
        {snapshots.map((s) => (
          <li
            key={s.id}
            className="flex items-center justify-between gap-4 rounded-md bg-surface-2 p-3"
          >
            <div>
              <p className="font-mono text-[12px] tabular-nums text-text-1">
                {new Date(s.takenAt).toLocaleString()}
              </p>
              <p className="text-[11px] text-text-3">trigger: {s.trigger}</p>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (
                  !confirm(
                    'Restore this snapshot? A pre-restore snapshot will be taken first.',
                  )
                )
                  return;
                try {
                  await restoreEpisodeSnapshot(episodeUuid, s.id);
                  toast.ok('Restored. Reloading episode…');
                  void refresh();
                } catch (err) {
                  toast.err(err instanceof Error ? err.message : 'Restore failed');
                }
              }}
              className="rounded-md bg-surface-3 px-3 py-1.5 text-[12px] font-medium text-text-1 transition-colors hover:brightness-110"
            >
              Restore
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// -------- Shared bits --------

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-[20px] font-semibold text-text-1">{title}</h1>
      {description && <p className="mt-1 text-[13px] text-text-3">{description}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  mono = false,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  mono?: boolean;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={clsx(
        'w-full rounded-md bg-surface-2 px-3 py-2 text-[13px] text-text-1 placeholder:text-text-4 outline-none ring-1 ring-line-2 focus:ring-line-3',
        mono && 'font-mono text-[12px]',
      )}
    />
  );
}
